import type { LocaleCode } from "../types";

import { deriveLocaleMetadata } from "../country/derive-locale-metadata";
import { getLocaleRegistry } from "../registry/locale-registry";

const ENVIRONMENT_HOSTNAME_PREFIXES = [
  "local.",
  "dev.",
  "staging.",
  "www.",
] as const;

/**
 * Normalizes a hostname for registry domain matching. Strips the first matching
 * environment prefix (`local.`, `dev.`, `staging.`, `www.`) and lowercases the
 * host. Vercel preview hostnames (e.g. `*.preview.honorcare.dev`) pass through
 * unchanged and do not match registry domains — locale for those requests is
 * resolved via path prefix, env, or default in `resolveLocale`.
 */
function normalizeHostname(hostname: string): string {
  if (!hostname) {
    return "";
  }

  const lower = hostname.toLowerCase().trim();
  let withoutPort = lower.split(":")[0] ?? lower;

  for (const prefix of ENVIRONMENT_HOSTNAME_PREFIXES) {
    if (withoutPort.startsWith(prefix)) {
      withoutPort = withoutPort.slice(prefix.length);
      break;
    }
  }

  return withoutPort;
}

/** Resolves locale from production or environment-prefixed registry hostnames. */
function resolveLocaleCodeFromHostname(hostname: string): LocaleCode | null {
  const normalized = normalizeHostname(hostname);

  if (!normalized) {
    return null;
  }

  for (const code of Object.keys(getLocaleRegistry()) as LocaleCode[]) {
    const { domain } = deriveLocaleMetadata(code);

    // Exact apex match plus subdomain suffix is intentional: env hosts such as
    // dev.homeinstead.com resolve to the parent locale without listing every
    // subdomain.
    if (normalized === domain || normalized.endsWith(`.${domain}`)) {
      return code;
    }
  }

  return null;
}

export { resolveLocaleCodeFromHostname };
